/**
 * Customer Insights Page
 * Deep analytics and customer behavior insights
 *
 * Features:
 * - Customer segmentation
 * - RFM (Recency, Frequency, Monetary) analysis
 * - Churn prediction
 * - Top customers
 * - Behavioral patterns
 * - Actionable recommendations
 */

import React, { useState, useEffect } from 'react';
import { Card, Badge, toast } from '../components/ui';
import {
  HourlyPatternsChart,
  TierDistributionChart,
} from '../components/Charts';
import { TierBadge } from '../components/ui/TierBadge';
import { businessApi } from '../lib/api';

export const InsightsPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [apiData, setApiData] = useState<any>(null);
  const [, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  const SEGMENT_COLOR_MAP: Record<string, string> = {
    champions: 'bg-purple-500',
    loyal: 'bg-blue-500',
    promising: 'bg-green-500',
    'at-risk': 'bg-orange-500',
    hibernating: 'bg-red-500',
  };

  const loadInsights = async () => {
    setIsLoading(true);
    setApiError(null);
    try {
      const response = await businessApi.getInsights({ range: timeRange });
      const raw = (response.data as any).data;
      const segs = (raw.segments ?? []).map((s: any) => ({
        ...s,
        color: SEGMENT_COLOR_MAP[s.id] ?? 'bg-gray-400',
      }));
      setApiData({ ...raw, segments: segs });
    } catch (err: any) {
      setApiError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load insights');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInsights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const segments = apiData?.segments ?? [];
  const topCustomers = apiData?.topCustomers ?? [];
  const insights = apiData?.insights ?? [];
  const hourlyPatterns = apiData?.hourlyPatterns ?? [];
  const tierDistribution = apiData?.tierDistribution ?? [];

  return (
    <div className="flex flex-col gap-8">
      {apiError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <p className="text-sm text-red-700">{apiError}</p>
          <button onClick={loadInsights} className="ml-4 px-3 py-1 text-sm font-medium text-red-700 border border-red-300 rounded-md hover:bg-red-100 transition-colors">
            Retry
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">Customer Insights</h1>
          <p className="text-base mt-2 text-black-lighter">
            Understand your customers and grow your business
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="inline-flex rounded-lg p-1 self-start sm:self-auto bg-cream-100 border border-border-light">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
                timeRange === range
                  ? 'bg-white text-black shadow-sm'
                  : 'bg-transparent text-black-lighter hover:text-black'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Key Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {insights.map((insight: any) => (
          <Card key={insight.title}>
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="text-3xl">{insight.icon}</div>
                <Badge
                  variant={
                    insight.priority === 'high'
                      ? 'error'
                      : insight.priority === 'medium'
                      ? 'warning'
                      : 'default'
                  }
                >
                  {insight.priority}
                </Badge>
              </div>
              <p className="text-h3 font-bold text-black mb-1">{insight.value}</p>
              <p className="text-xs font-semibold uppercase text-black-lighter mb-2 tracking-wide">
                {insight.title}
              </p>
              <p className="text-sm text-black-light mb-3">{insight.description}</p>
              <button className="text-sm font-semibold text-orange-400 hover:text-orange-500 transition-colors">
                {insight.action} →
              </button>
            </div>
          </Card>
        ))}
      </div>

      {/* Customer Segmentation */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h4 font-semibold text-black">Customer Segmentation</h3>
          <p className="text-sm mt-1 text-black-lighter">
            RFM analysis: Recency, Frequency, Monetary value
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {segments.map((segment: any) => (
              <div
                key={segment.id}
                className="relative p-4 bg-cream-50 rounded-lg border-2 border-border-light hover:border-orange-200 transition-all cursor-pointer group"
              >
                <div className={`w-full h-2 ${segment.color} rounded-full mb-3`} />
                <p className="text-base font-semibold text-black mb-1">{segment.name}</p>
                <p className="text-xs text-black-lighter mb-3">{segment.description}</p>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-black-lighter">Count:</span>
                    <span className="text-xs font-semibold text-black">{segment.count}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-black-lighter">Share:</span>
                    <span className="text-xs font-semibold text-black">{segment.percentage}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-black-lighter">Avg LTV:</span>
                    <span className="text-xs font-semibold text-black">
                      ${segment.avgLifetimeValue}
                    </span>
                  </div>
                </div>

                <button className="mt-3 w-full px-3 py-2 bg-white border border-border-light rounded-md text-sm font-medium text-black hover:bg-cream-50 transition-colors" onClick={() => toast.info('Segment drill-down coming soon. Use Customers page to filter manually.')}>
                  View Customers
                </button>
              </div>
            ))}
          </div>

          {segments.length > 0 && (() => {
            const top = [...segments].sort((a: any, b: any) => b.avgLifetimeValue - a.avgLifetimeValue)[0];
            return (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Insight:</strong> Your &quot;{top.name}&quot; segment has the highest average lifetime value
                  (${top.avgLifetimeValue?.toFixed(0)} avg).
                </p>
              </div>
            );
          })()}
        </div>
      </Card>

      {/* Top Customers */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h4 font-semibold text-black">Top Customers</h3>
          <p className="text-sm mt-1 text-black-lighter">
            Your most valuable customers by lifetime value
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                  Customer
                </th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                  Tier
                </th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                  Lifetime Value
                </th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                  Visits
                </th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                  Last Visit
                </th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-black-lighter tracking-wide">
                  Trend
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light">
              {topCustomers.map((customer: any) => (
                <tr key={customer.id} className="hover:bg-cream-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-base font-medium text-black">{customer.name}</p>
                      <p className="text-sm text-black-lighter">{customer.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <TierBadge tier={customer.tier} size="sm" />
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-base font-semibold text-black">
                      ${customer.lifetimeValue.toLocaleString()}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-base text-black">{customer.totalVisits}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-base text-black">{customer.lastVisit}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      {customer.trend === 'up' && (
                        <span className="text-green-500">↑</span>
                      )}
                      {customer.trend === 'down' && (
                        <span className="text-red-500">↓</span>
                      )}
                      {customer.trend === 'stable' && (
                        <span className="text-black-lighter">→</span>
                      )}
                      <span className="text-sm text-black-lighter capitalize">{customer.trend}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Behavioral Patterns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6 border-b border-border-light">
            <h3 className="text-h4 font-semibold text-black">Peak Hours</h3>
            <p className="text-sm mt-1 text-black-lighter">
              When your customers are most active
            </p>
          </div>
          <div className="p-6">
            <HourlyPatternsChart data={hourlyPatterns} />
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-900">
                <strong>✨ Recommendation:</strong> Schedule your challenges and campaigns to
                launch during peak hours (12pm-2pm, 6pm-8pm) for maximum engagement.
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6 border-b border-border-light">
            <h3 className="text-h4 font-semibold text-black">Tier Distribution</h3>
            <p className="text-sm mt-1 text-black-lighter">
              Customer distribution across tiers
            </p>
          </div>
          <div className="p-6">
            <TierDistributionChart data={tierDistribution} />
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-900">
                <strong>🎯 Goal:</strong> Move more customers into Buffies VIP and above by running
                tier-specific challenges and seasonal promotions.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default InsightsPage;
